/**
 * InvestigationReportCard — a saved Deep Research report on its investigation (PRD-0076).
 *
 * The narrative renders as markdown; an `[S#]` citation scrolls to its source in the list
 * below and flashes it. Sources are named (document and time in the recording, entity,
 * source rows) and open where they point; an inline `node:` link opens the entity. A link
 * leads back to the research the report came from. The findings of that research are not
 * part of the report (ADR-0097), so there are no `[F#]` markers to follow, and the report
 * was saved without its own `# title`: the page shows it as the investigation's title.
 */

import { ArrowUpRight, FileText } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { EntityDetailSheet } from '@/components/investigations/entity-detail/EntityDetailSheet'
import { ReportMarkdown } from '@/components/research/ReportMarkdown'
import { EvidenceCard } from '@/components/search/detail/EvidenceCard'
import { Section } from '@/components/search/detail/Section'
import { Button } from '@/components/ui/button'
import { researchPath } from '@/features/analytic-hypotheses/paths'
import { useFlashIndex } from '@/hooks/use-flash-index'
import type { Investigation } from '@/types/investigation'

export function InvestigationReportCard({ inv }: { inv: Investigation }) {
  const { t } = useTranslation()
  const [sourceFlash, setSourceFlash] = useFlashIndex()
  const [openEntityId, setOpenEntityId] = useState<string | null>(null)
  const evidence = inv.evidence ?? []
  const researchHref = inv.hypothesisId ? researchPath(inv.caseId, inv.hypothesisId) : null
  // Stable, so the memoised markdown is not re-parsed on every flash.
  const openSource = useCallback((index: number) => setSourceFlash(index - 1), [setSourceFlash])

  if (!inv.reportMd) return null

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={t('investigations.report.title')}
        hint={t('investigations.report.hint', { count: evidence.length })}
      >
        <div className="flex flex-col gap-4">
          <ReportMarkdown
            markdown={inv.reportMd}
            findingCount={0}
            sourceCount={evidence.length}
            onOpenSource={openSource}
            onOpenEntityRef={setOpenEntityId}
          />
          {researchHref && (
            <Button asChild variant="outline" size="sm" className="group self-start max-md:h-9">
              <Link to={researchHref}>
                <FileText className="size-3.5" />
                {t('investigations.report.openResearch')}
                <ArrowUpRight className="size-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Button>
          )}
        </div>
      </Section>

      {evidence.length > 0 && (
        <EvidenceCard
          evidence={evidence}
          highlightIndex={sourceFlash}
          onOpenEntity={setOpenEntityId}
        />
      )}

      {openEntityId && (
        <EntityDetailSheet
          caseId={inv.caseId}
          nodeId={openEntityId}
          onClose={() => setOpenEntityId(null)}
        />
      )}
    </div>
  )
}
